import { computed, ref, watch, type ComputedRef, type Ref } from 'vue';
import { injectLocal, provideLocal } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { useProductFlags } from '~/modules/featureControl/composables/useProductFlags';
import useUpc from '~/modules/upc/composables/useUpc';
import useExperiments from '~/stores/useExperimentsStore';
import { BIG_TICKET_DELIVERY_METHODS_REGISTRY } from '../consts/providers';
import { useAvailabilityBigTicket } from './useAvailabilityBigTicket';
import useConsolidatedMemberStore from '~/stores/useConsolidatedMemberStore';
import { formatPrice } from '~/utils/commonUtils';
import { useBigTicketDeliveryMethodsFetch, type BigTicketDeliveryMethod } from './useBigTicketDeliveryMethodsFetch';

export type ResolvedAdditionalFee = {
  deliveryMethod: {
    type: string;
    description: string;
    abbreviation: string;
    totalDeliveryFee: number;
    totalPriceWithDeliveryFee?: number;
  };
  surchargeAmount?: number;
  surchargeEligible?: boolean;
} | null;

export type BigTicketDeliveryMethodsReturnData = {
  deliveryMethods: ComputedRef<BigTicketDeliveryMethod[]>;
  selectedDeliveryType: Ref<string | null>;
  isBigTicketDeliveryActive: ComputedRef<boolean>;
  isDeliveryUpdatePending: Ref<boolean>;
  selectedDeliveryMethod: ComputedRef<BigTicketDeliveryMethod | null>;
  junkFees: ComputedRef<string>;
  bigTicketCurrentDeliveryMessage: ComputedRef<string>;
  resolvedAdditionalFee: ComputedRef<ResolvedAdditionalFee>;
  includeDeliveryMethodInATBPayload: () => { selectedDeliveryType?: string };
};

/**
 * Custom injection hook to retrieve Big Ticket Delivery Methods state machine from provide context.
 */
export const useBigTicketDeliveryMethods = (): BigTicketDeliveryMethodsReturnData => {
  const bigTicketDeliveryMethods = injectLocal<BigTicketDeliveryMethodsReturnData>(BIG_TICKET_DELIVERY_METHODS_REGISTRY);
  if (bigTicketDeliveryMethods === undefined) {
    throw new TypeError('useBigTicketDeliveryMethods() is called without provider, useBigTicketDeliveryMethodsProvide.');
  }
  return bigTicketDeliveryMethods;
};

/**
 * Provides the Big Ticket Delivery Methods state machine, chip selection tracking, and provider local registration.
 */
export const useBigTicketDeliveryMethodsProvide = (initDeliveryType?: string | null): void => {
  const { bigTicketAvailableForCurrentUpc, getCurrentDeliveryMessage, currentZipCodeAvailabilityData } = useAvailabilityBigTicket();
  const { selectedUpcData } = useUpc();
  const { isBigTicketItemFlag } = useProductFlags();
  const { experimentFlags } = useExperiments();
  const { memberFlags } = storeToRefs(useConsolidatedMemberStore());
  const selectedDeliveryType = ref<string | null>(initDeliveryType ?? null);

  // WGD is active if the experiment is on (KS + 3214-21), the current item is a big ticket product, and it is not a marketplace item
  const isBigTicketDeliveryActive = computed(
    () =>
      !!experimentFlags.isBigTicketDeliveryMethodsEnabledExp &&
      !!(isBigTicketItemFlag || memberFlags.value?.isBigTicketItemFlag) &&
      !selectedUpcData.value?.marketplaceFlag,
  );

  const { deliveryMethods, pending: isDeliveryUpdatePending } = useBigTicketDeliveryMethodsFetch(isBigTicketDeliveryActive);

  // Retrieves the full delivery method details matching the user's active selected type.
  const selectedDeliveryMethod = computed(
    () => (selectedDeliveryType.value && deliveryMethods.value.find((m) => m.type === selectedDeliveryType.value)) || null,
  );
  // Extracts and formats the selected delivery fee to 2 decimal places w/ dollar sign.
  const junkFees = computed(() => (selectedDeliveryMethod.value ? formatPrice(selectedDeliveryMethod.value.totalPriceWithDeliveryFee) : ''));

  const bigTicketCurrentDeliveryMessage = computed<string>(() => getCurrentDeliveryMessage(isBigTicketDeliveryActive.value));

  const resolvedAdditionalFee = computed<ResolvedAdditionalFee>(() => {
    const apiAdditionalFee = currentZipCodeAvailabilityData?.value?.products?.additionalFee;

    if (apiAdditionalFee && selectedDeliveryMethod.value) {
      const method = selectedDeliveryMethod.value;
      return {
        deliveryMethod: {
          type: method?.type ?? null,
          description: method?.description ?? '',
          abbreviation: method?.abbreviation ?? '',
          totalDeliveryFee: method?.totalDeliveryFee ?? 0,
          totalPriceWithDeliveryFee: method?.totalPriceWithDeliveryFee ?? null,
        },
      };
    }

    if (apiAdditionalFee) {
      return apiAdditionalFee;
    }

    return null;
  });

  watch(
    () => deliveryMethods.value,
    (newDeliveryMethods) => {
      // Return early if WGD is inactive, if no methods were returned, or if the user already
      // has a valid selection active. This prevents overwriting their active choice on background refetches.
      if (!isBigTicketDeliveryActive.value || !newDeliveryMethods.length || selectedDeliveryMethod.value) return;

      const { type } = newDeliveryMethods[0] as BigTicketDeliveryMethod;

      // Auto-default the active selection to the first available supported delivery method
      if (selectedDeliveryType.value !== type) {
        selectedDeliveryType.value = type;
      }
    },
  );
  const includeDeliveryMethodInATBPayload = (): { selectedDeliveryType?: string } => {
    if (!bigTicketAvailableForCurrentUpc.value || !deliveryMethods.value?.length || !selectedDeliveryType.value) {
      return {};
    }

    return { selectedDeliveryType: selectedDeliveryType.value };
  };

  provideLocal<BigTicketDeliveryMethodsReturnData>(BIG_TICKET_DELIVERY_METHODS_REGISTRY, {
    deliveryMethods,
    selectedDeliveryType,
    isBigTicketDeliveryActive,
    isDeliveryUpdatePending,
    selectedDeliveryMethod,
    junkFees,
    bigTicketCurrentDeliveryMessage,
    resolvedAdditionalFee,
    includeDeliveryMethodInATBPayload,
  });
};
